import React from 'react';

// Body content for the post:
// "How Plum makes AI design better than ChatGPT, Gemini, or Claude for home design"
//
// Plain JSX (no markdown dependency), matching the project's no-extra-deps
// convention. Headings use semantic tags so the article is crawlable and
// accessible. Internal links are real <a href> anchors that resolve to the
// public routes handled in App.js.
function AiDesignVsChatbots() {
  return (
    <>
      <p>
        General-purpose AI assistants like ChatGPT, Gemini, and Claude are
        remarkable. Ask any of them for ten kitchen color palettes or a list of
        flooring options and you will get a thoughtful answer in seconds. So a
        fair question is: if those tools are free and powerful, why use a
        purpose-built product like Plum to plan a renovation at all?
      </p>
      <p>
        The short answer is that a chat window is the start of a renovation, not
        the finish. A great renovation needs structure, memory, pictures of your
        actual space, real specifications, and a document a contractor can
        actually bid on. That is the gap Plum is built to close.
      </p>

      <h2>General chatbots are powerful, but they forget</h2>
      <p>
        A renovation is a long project with hundreds of small decisions —
        cabinet style, counter material, tile layout, lighting, plumbing
        fixtures, paint, timeline, and budget. In a general chat, those
        decisions live in a scroll-back you have to keep re-explaining. Start a
        new conversation and the context is gone.
      </p>
      <p>
        Plum keeps a structured, persistent project instead of a transcript.
        Every decision you make is captured as part of your project — the
        requirements, the design choices, the scope, and the task list all
        update as you talk. Come back a week later and the project is exactly
        where you left it, not buried in chat history.
      </p>

      <h2>Plum asks the questions you didn't know to ask</h2>
      <p>
        The hardest part of planning a renovation is not getting answers — it is
        knowing the right questions. A blank chat box waits for you to be the
        expert. Plum runs a guided consultation that draws on built-in domain
        knowledge across kitchens, bathrooms, flooring, electrical, plumbing,
        millwork, HVAC, paint, and more, so it surfaces the details that drive
        cost and quality before they become expensive surprises.
      </p>
      <p>
        Instead of a generic "here are some ideas," you get the targeted
        follow-ups a good designer would ask: how the space is used, what stays
        and what goes, where the budget should flex, and which specifications a
        contractor will need to quote accurately.
      </p>

      <h2>Design grounded in your actual space</h2>
      <p>
        Ask a general chatbot to "redesign my kitchen" and it has nothing to
        work from but words. Plum is built around your real space. Add photos of
        the room, along with colors, textures, and products you love, and Plum
        generates design visualizations grounded in what you actually have —
        producing before-and-after imagery, mood boards, and floor plans tied to
        your project rather than a generic stock render.
      </p>
      <p>
        That grounding matters. A picture of <em>your</em> kitchen reimagined is
        worth far more than a beautiful image of someone else's, both for making
        confident decisions and for showing a contractor exactly what you want.
      </p>

      <h2>Real deliverables, not just text</h2>
      <p>
        The output of a chat is more chat. The output of Plum is a project
        package you can act on:
      </p>
      <ul>
        <li>
          <strong>A contractor-ready scope of work</strong> that organizes every
          decision into clear specifications and measurements, with before and
          after photos attached.
        </li>
        <li>
          <strong>A cost estimate</strong> built from your specific scope, so
          you walk into quotes with a realistic number.
        </li>
        <li>
          <strong>A task plan</strong> that sequences the work so nothing gets
          done out of order.
        </li>
        <li>
          <strong>Shareable designs</strong> you can send to a partner,
          designer, or contractor.
        </li>
      </ul>
      <p>
        These are the artifacts that move a project forward. Recreating them by
        hand from a chat transcript is exactly the tedious work Plum removes.
      </p>

      <h2>Built for the whole journey — including finding a contractor</h2>
      <p>
        A general model can describe how to hire a contractor. Plum is designed
        to help you actually do it: a detailed, standardized scope means the
        contractors you talk to are quoting the same project, so you get
        comparable, competitive bids and far fewer costly change orders later.
        We dig into that in our companion article,{' '}
        <a href="/blog/finding-contractors-problem">
          The problems of finding contractors — and how Plum addresses them
        </a>
        .
      </p>

      <h2>The bottom line</h2>
      <p>
        Use ChatGPT, Gemini, or Claude when you want a quick idea. Use Plum when
        you want to plan a renovation you will actually build. Plum takes the raw
        capability of modern AI and wraps it in structure, memory, visuals, and
        deliverables purpose-built for home design — and it is completely free
        for homeowners.
      </p>
      <p>
        <a href="/home">Start your free project with Plum →</a>
      </p>
    </>
  );
}

export default AiDesignVsChatbots;
